/**
 * @fileoverview カートの添付品（無料おまけ）の数量を自動算出し、選択パネルを描画する。
 *
 * 【仕様書との対応】
 * このスクリプトは「【PDS】添付品_0515.xlsx」の仕様に基づく。
 * 仕様書のセクション番号を (§1.3) のように記す。
 *
 * 【処理の流れ（1 添付率プロファイルグループあたり）】
 *
 *  ┌─────────────────────────────────────────────────────────────────────────┐
 *  │ 前提: 添付率プロファイル (§1.12) でグループ分けし、添付品行・対象外製品を除外 (§1.2) │
 *  ├─────────────────────────────────────────────────────────────────────────┤
 *  │ STEP 1  全体枠の決定 ── floor(グループ合計 N × 添付率)  (§1.3)         │
 *  │ STEP 2  個別上限の算出 ── ceil(行の購入数 × capRate)    (§1.4)         │
 *  │ STEP 3  一次配分 ── floor(行の購入数 × 添付率)          (§1.5)         │
 *  │ STEP 4  二次配分 ── 残り枠をラウンドロビンで充当        (§1.5)         │
 *  ├─────────────────────────────────────────────────────────────────────────┤
 *  │ パネル描画: 自動配分 > 0 の行と同点タイの行を候補として表示             │
 *  │ カート反映: ユーザー選択を Shopify Cart API で同期                      │
 *  └─────────────────────────────────────────────────────────────────────────┘
 *
 * 【同点タイ】
 * STEP 4 はティア（同じ購入数の行の集まり）単位で 1 個ずつ均等配分する。
 * あるティア内の対象行数 > 残り枠のとき、自動では決められないため
 * そのティアの全行を「同点タイ」としてユーザーに選ばせる。
 * タイは構造上、同時に最大 1 ティアでしか発生しない。
 *
 * 【依存】
 * - window.pdsAttachmentRules …… Liquid (pds-attached-gifts-config) から注入されるルール設定
 * - カート行 input の data-pds-* 属性 …… Liquid スニペットが出力
 *
 * 【メタフィールド構成】
 * Product:  custom.pds_attachment_rate_profile   … 添付率プロファイル (§1.12)
 *           custom.pds_is_attached_gift          … true なら添付品 SKU（N に含めない）
 *           custom.pds_exclude_attachment_count   … true なら数量加算対象外 (§1.2)
 * Variant:  custom.pds_attached_gift_variant     … 対応する添付品バリアントへの参照 (§1.13)
 */

/**
 * 添付率の段階定義（rateTiers の 1 要素）。
 * 例: { minN: 10, rate: 0.10 } → 合計 N ≧ 10 なら添付率 10%
 * @typedef {object} PdsAttachmentRateTier
 * @property {number} minN - この段が有効になる最低個数
 * @property {number} rate - この段の添付率（0〜1）
 */

/**
 * 1 添付率プロファイルグループ分のルール定義。
 * 同一添付率プロファイル（例: 10+1,30+6）の商品を 1 グループにまとめる。
 * @typedef {object} PdsAttachmentGroupRule
 * @property {string} id - グループの識別名（ログ用。メタフィールド値と一致しなくてよい）
 * @property {string[]} [matchProfiles] - 添付率プロファイルの一覧（省略時は id をそのまま使う）
 * @property {PdsAttachmentRateTier[]} rateTiers - 合計個数 N に応じた添付率テーブル (§1.3)
 * @property {number} [capRate] - 個別上限の計算に使う率 (§1.4)。省略時は添付率と同じ値を使用
 */

/**
 * ルール全体の設定オブジェクト（Liquid → window.pdsAttachmentRules）。
 * @typedef {object} PdsAttachmentRules
 * @property {boolean} [enabled] - false で機能全体を無効化
 * @property {boolean} [debug] - true でコンソールに計算結果を出力
 * @property {{ incompleteSelection?: string, syncFailed?: string }} [strings] - UI 文言（[remaining] 置換）
 * @property {PdsAttachmentGroupRule[]} groups - グループごとのルール配列
 */

/**
 * カート 1 行分の計算用データ（HTML の data 属性から生成）。
 * @typedef {object} PdsCartLineForAttachment
 * @property {string} lineKey - 行の一意キー（data-pds-attachment-line-key）
 * @property {string} productId - Shopify 商品 ID
 * @property {number} quantity - 購入個数
 * @property {string} attachmentProfile - 添付率プロファイル（メタフィールド pds_attachment_rate_profile）
 * @property {string} [lineTitle] - 表示用の商品名
 * @property {boolean} isGiftLine - true = 添付品 SKU の行（合計 N に含めない）
 * @property {boolean} excludeFromAttachmentCount - true = 数量加算対象外 (§1.2)
 * @property {string} [attachedGiftVariantId] - 対応する添付品バリアント ID
 * @property {string} [attachedGiftDisplayTitle] - パネルに出す添付品の表示名（商品名＋バリエーション）
 * @property {string} [attachedGiftProductTitle] - 添付品の商品名のみ
 * @property {{ name: string, value: string }[]} [attachedGiftVariantOptions] - バリエーション（通常カートの options_with_values 相当）
 * @property {string} [attachedGiftImageUrl] - 添付品の画像 URL
 * @property {string} [cartVariantId] - カート行の有料バリアント ID（data-quantity-variant-id）
 */

/**
 * 1 グループ分の計算結果（パネル表示・カート反映に使う）。
 * @typedef {object} PdsGroupAllocationResult
 * @property {string} groupId - グループ ID
 * @property {string[]} candidateLineKeys - パネルに表示する行キーの配列
 * @property {number} groupTotalN - グループ合計個数 N
 * @property {number} appliedRate - 適用された添付率（0〜1）
 * @property {number} capRate - 個別上限の計算に使った率
 * @property {number} wholeSlot - 全体枠（STEP 1 の結果）
 * @property {number} selectionPicksApplied - ユーザーが現在選択している合計個数
 * @property {number} remainderLeft - あと何個足せるか（wholeSlot − selectionPicksApplied）
 * @property {Record<string, number>} machineFinal - 行キー → 自動配分の最終個数
 * @property {Record<string, object>} byLine - 行キー → 行ごとの詳細オブジェクト
 *   - primary:       一次配分の個数
 *   - cap:           個別上限（STEP 2）
 *   - headroom:      一次後にまだ cap まで足せる余地
 *   - pickMax:       ユーザーが選べる最大個数（＋ボタンの上限）
 *   - machineFinal:  自動配分の最終個数（一次＋二次）
 *   - selectionPick: ユーザーが実際に選んでいる個数
 */

(function () {
  // ═══════════════════════════════════════════════════════════════════════════
  // 公開 API（window に露出するもの）
  //   window.pdsAttachmentRules               … Liquid が注入するルール設定（入力）
  //   window.pdsAttachmentUserPicks            … ユーザーの選択状態（入出力）
  //   window.pdsComputeAttachmentAllocations   … 計算を実行する関数
  //   window.pdsGetAttachmentAllocationsFromForms … フォームから読んで計算する関数
  //   window.pdsAttachmentAdjustPick           … ± ボタンで選択を増減
  //   window.pdsRefreshAttachmentSelectionPanel … パネルを手動で再描画
  // ═══════════════════════════════════════════════════════════════════════════
  // ─────────────────────────────────────────────────────────────────────────
  // ルール取得
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Liquid が `window.pdsAttachmentRules` に入れたルールを取得する。
   * @returns {PdsAttachmentRules|undefined}
   */
  function getRules() {
    return window.pdsAttachmentRules;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 算術ユーティリティ（浮動小数誤差の補正付き）
  //
  // JavaScript の浮動小数では 0.29 * 100 = 28.999… のように floor/ceil が
  // 1 ずれることがある。1e-9 の微小補正でこれを回避する。
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * floor(q × rate)。全体枠 (STEP 1) と一次配分 (STEP 3) で使用。
   * @param {number} q - 個数
   * @param {number} rate - 率（0〜1）
   * @returns {number} 切り捨て結果
   */
  function floorMul(q, rate) {
    return Math.floor(q * rate + 1e-9);
  }

  /**
   * ceil(q × rate)。個別上限 cap (STEP 2) で使用。
   * @param {number} q - 個数
   * @param {number} rate - 率（0〜1）
   * @returns {number} 切り上げ結果
   */
  function ceilMul(q, rate) {
    return Math.ceil(q * rate - 1e-9);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // グループ判定・合計 N の算出
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * 合計個数 N がどの段階に該当するかを判定し、添付率を返す (§1.3)。
   * rateTiers は minN 昇順を想定。条件を満たす最後の段が採用される。
   * @param {number} groupTotalN - グループ合計 N
   * @param {PdsAttachmentGroupRule} groupRule
   * @returns {number} 添付率（0〜1）。rateTiers が空なら 0
   */
  function resolveRateForN(groupTotalN, groupRule) {
    let resolvedRate = 0;
    const tiers = groupRule.rateTiers || [];
    for (let i = 0; i < tiers.length; i++) {
      if (groupTotalN >= tiers[i].minN) resolvedRate = tiers[i].rate;
    }
    return resolvedRate;
  }

  /**
   * 添付率プロファイルを正規化（前後空白除去）して比較用にそろえる。
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeAttachmentProfile(value) {
    return String(value || '').trim();
  }

  /**
   * グループに属する有効な行だけを返す。除外条件:
   * - 添付品 SKU の行（isGiftLine）
   * - 数量加算対象外の行（excludeFromAttachmentCount）
   * - 添付率プロファイルが一致しない行
   * - 添付品バリアント ID が未設定の行（配分対象にならない）
   * @param {PdsCartLineForAttachment[]} lines - 全カート行
   * @param {PdsAttachmentGroupRule} groupRule
   * @returns {PdsCartLineForAttachment[]} フィルタ後の行
   */
  function filterLinesForGroup(lines, groupRule) {
    const profiles =
      Array.isArray(groupRule.matchProfiles) && groupRule.matchProfiles.length
        ? groupRule.matchProfiles
        : [groupRule.id];
    const allowed = new Set(profiles.map((p) => normalizeAttachmentProfile(p)).filter(Boolean));
    return lines.filter((line) => {
      if (line.isGiftLine || line.excludeFromAttachmentCount) return false;
      const profile = normalizeAttachmentProfile(line.attachmentProfile);
      if (profile === '' || !allowed.has(profile)) return false;
      return String(line.attachedGiftVariantId || '').trim() !== '';
    });
  }

  /**
   * 対象行の購入個数を合算する（= グループ合計 N）。
   * @param {PdsCartLineForAttachment[]} lines - filterLinesForGroup 済みの行
   * @returns {number}
   */
  function sumQuantity(lines) {
    return lines.reduce((sum, line) => sum + line.quantity, 0);
  }

  /**
   * 個別上限 (STEP 2) の計算に使う率を返す (§1.4)。
   * groupRule.capRate が明示されていればその値、なければ添付率と同じ。
   * @param {number} appliedRate - resolveRateForN で決まった添付率
   * @param {PdsAttachmentGroupRule} groupRule
   * @returns {number}
   */
  function resolveCapRate(appliedRate, groupRule) {
    if (typeof groupRule.capRate === 'number') return groupRule.capRate;
    return appliedRate;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 一次配分の合計を全体枠に収める（安全弁）
  //
  // 数学的には floor の和 ≦ floor(合計×率) になりやすいが、浮動小数や将来の
  // capRate 変更で不等式が崩れる可能性がある。そのため防御的に合計を全体枠以下に
  // クランプしてから二次配分に渡す。
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * 一次配分の合計が wholeSlot を超えている場合、多い行から 1 ずつ減らす。
   * さらに各行が cap を超えないよう調整する。
   * @param {Record<string, number>} primary - 行キー → 一次配分の個数
   * @param {Record<string, number>} caps - 行キー → 個別上限
   * @param {number} wholeSlot - 全体枠
   * @param {PdsCartLineForAttachment[]} groupLines - 対象行
   * @returns {Record<string, number>} 調整後の一次配分
   */
  function clampPrimaryToWholeSlot(primary, caps, wholeSlot, groupLines) {
    const next = { ...primary };
    let primarySum = 0;
    groupLines.forEach((line) => {
      primarySum += next[line.lineKey] || 0;
    });

    if (primarySum <= wholeSlot) return next;

    // 超過分を解消: 一次が多い行から 1 ずつ引く（同数なら行キー辞書順で決定論的に）
    const lineKeys = groupLines.map((line) => line.lineKey);
    let guard = 0;
    while (primarySum > wholeSlot && guard++ < 10000) {
      let progressed = false;
      const sortedKeys = lineKeys
        .filter((k) => (next[k] || 0) > 0)
        .sort((a, b) => (next[b] || 0) - (next[a] || 0) || a.localeCompare(b));
      for (let i = 0; i < sortedKeys.length; i++) {
        const k = sortedKeys[i];
        next[k] -= 1;
        primarySum -= 1;
        progressed = true;
        break;
      }
      if (!progressed) break;
    }

    groupLines.forEach((line) => {
      const k = line.lineKey;
      if ((next[k] || 0) > (caps[k] || 0)) next[k] = caps[k] || 0;
    });
    return next;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 4: 二次配分（ラウンドロビン＝順番に回して配分）
  //
  // 仕組み:
  //   1. 行を購入数降順にソートし、同じ購入数の連続区間を「ティア（tier：購入数帯）」とする
  //   2. 上位ティアから順に、ティア内の全 eligible（まだ配分可能な）行に +1 する（1 スイープ＝一巡）
  //   3. remaining（残り全体枠）が尽きるか、全行が cap（個別上限）に達するまでスイープを繰り返す
  //   4. あるティアで eligible 行数 > remaining なら「同点タイ」→ 自動配分停止
  //
  // 同点タイの構造的性質:
  //   ティアは上から順に処理するため、タイは同時に最大 1 ティアでしか起きない。
  //   あるティアでタイが発生した時点で remaining（残り全体枠）= 0 になり、以降のティアは処理されない。
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * 一次配分の結果から始めて、全体枠に届くまでラウンドロビンで +1 する。
   * @param {object} params
   * @param {PdsCartLineForAttachment[]} params.lines - 対象行
   * @param {Record<string, number>} params.primary - 一次配分（調整済み）
   * @param {Record<string, number>} params.caps - 個別上限
   * @param {number} params.wholeSlot - 全体枠
   * @returns {{ allocated: Record<string, number>, remainderLeft: number, tieLineKeys: string[], tieRemaining: number }}
   *   - allocated:      最終的な行キー → 個数
   *   - remainderLeft:  配り切れなかった残り（通常 0）
   *   - tieLineKeys:    同点タイの行キー配列（タイなしなら空）
   *   - tieRemaining:   タイで配れなかった残り枠数
   */
  function distributeRemainder({ lines, primary, caps, wholeSlot }) {
    /** @type {Record<string, number>} */
    const allocated = {};
    lines.forEach((line) => {
      allocated[line.lineKey] = primary[line.lineKey] || 0;
    });

    let used = 0;
    lines.forEach((line) => {
      used += allocated[line.lineKey] || 0;
    });
    let remaining = wholeSlot - used;

    // 購入数降順（同数なら行キー辞書順）でソート → 同じ quantity の連続区間がティア
    const order = [...lines].sort((a, b) => b.quantity - a.quantity || a.lineKey.localeCompare(b.lineKey));

    /** @type {string[]} */
    let tieLineKeys = [];
    let tieRemaining = 0;

    // ── スイープループ（外側 while = 全ティアを 1 巡） ──
    let guard = 0;
    while (remaining > 0 && guard++ < 10000) {
      let anyProgress = false;
      let hitTie = false;
      let i = 0;

      // ── ティア走査（内側 while） ──
      while (i < order.length && remaining > 0) {
        // ティアの範囲を特定（同じ quantity の連続区間）
        const tierQty = order[i].quantity;
        const tierStart = i;
        while (i < order.length && order[i].quantity === tierQty) i++;

        // ティア内で、既に個別上限 (cap) に達した行は配分不可なので除外する。
        // ここで絞った eligible 行の数と remaining を比較して、
        // 全員に +1 できるか（均等配分）、それとも足りないか（同点タイ）を判定する。
        const eligibleInTier = [];
        for (let j = tierStart; j < i; j++) {
          const key = order[j].lineKey;
          if ((allocated[key] || 0) < (caps[key] || 0)) {
            eligibleInTier.push(order[j]);
          }
        }

        if (eligibleInTier.length === 0) continue; // 全員 cap 到達済み → このティアをスキップ

        if (remaining >= eligibleInTier.length) {
          // 全員に +1 できる → 均等配分
          eligibleInTier.forEach((line) => {
            allocated[line.lineKey] = (allocated[line.lineKey] || 0) + 1;
            remaining -= 1;
          });
          anyProgress = true;
        } else {
          // 全員に配れない → 同点タイ（自動配分を停止し、ユーザー選択に委ねる）
          tieLineKeys = eligibleInTier.map((l) => l.lineKey);
          tieRemaining = remaining;
          remaining = 0;
          hitTie = true;
          break;
        }
      }

      if (hitTie || !anyProgress) break;
    }

    return { allocated, remainderLeft: remaining, tieLineKeys, tieRemaining };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ユーザー選択の正規化・永続化
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * ユーザーが選んだ個数を、行ごとの上限（pickMax）と全体枠（wholeSlot）に収まるよう正規化する。
   * 超過時は選択個数が多い行から優先的に減らす。
   * @param {string[]} candidateKeys - パネル候補行の行キー
   * @param {Record<string, number>} pickMax - 行キー → 選択上限
   * @param {number} wholeSlot - 全体枠
   * @param {Record<string, number>} rawPicks - 行キー → ユーザー入力の生の個数
   * @returns {Record<string, number>} 正規化後の選択個数
   */
  function clampAttachmentUserPicks(candidateKeys, pickMax, wholeSlot, rawPicks) {
    /** @type {Record<string, number>} */
    const picks = {};
    // 1) 行ごと: 0 以上の整数にし、pickMax でクランプ
    candidateKeys.forEach((k) => {
      const v = Math.max(0, Math.floor(Number(rawPicks[k]) || 0));
      const mx = Math.max(0, pickMax[k] || 0);
      picks[k] = Math.min(v, mx);
    });

    let sum = candidateKeys.reduce((s, k) => s + (picks[k] || 0), 0);
    if (sum <= wholeSlot) return picks;

    // 2) 全体: 合計が wholeSlot を超える場合、選択数が多い行から削る
    const keys = [...candidateKeys];
    keys.sort((a, b) => (picks[b] || 0) - (picks[a] || 0) || a.localeCompare(b));
    let over = sum - wholeSlot;
    for (let j = 0; j < keys.length && over > 0; j++) {
      const k = keys[j];
      while (over > 0 && (picks[k] || 0) > 0) {
        picks[k]--;
        over--;
      }
    }
    return picks;
  }

  /**
   * 計算結果を window.pdsAttachmentUserPicks に書き戻す。
   * 0 個の行は含めず、消えたグループ ID や行キーも自動的に掃除される。
   * @param {PdsGroupAllocationResult[]} results
   */
  function syncStoredPicksFromAllocation(results) {
    window.pdsAttachmentUserPicks = {};
    results.forEach((g) => {
      /** @type {Record<string, number>} */
      const next = {};
      Object.keys(g.byLine || {}).forEach((k) => {
        const sp = g.byLine[k].selectionPick || 0;
        if (sp > 0) next[k] = sp;
      });
      if (Object.keys(next).length) window.pdsAttachmentUserPicks[g.groupId] = next;
    });
  }

  /**
   * 2 つの選択マップをマージする。同一キーは b（第 2 引数）が優先。
   * @param {Record<string, Record<string, number>>|undefined} windowPicks - ベース
   * @param {Record<string, Record<string, number>>|undefined} requestOverride - 上書き
   * @returns {Record<string, Record<string, number>>}
   */
  function mergeUserPicks(windowPicks, requestOverride) {
    const a = windowPicks || {};
    const b = requestOverride || {};
    const ids = new Set([...Object.keys(a), ...Object.keys(b)]);
    /** @type {Record<string, Record<string, number>>} */
    const out = {};
    ids.forEach((gid) => {
      out[gid] = { ...(a[gid] || {}), ...(b[gid] || {}) };
    });
    return out;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // グループ計算のメイン関数（STEP 1〜4 + パネル情報の算出）
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * 1 グループ分の全計算をまとめて行い、パネル表示・カート反映に必要な情報を返す。
   *
   * 内部の処理順:
   *  1. グループ対象行を抽出し合計 N を算出
   *  2. STEP 1: 全体枠 = floor(N × 添付率)
   *  3. STEP 2: 各行の個別上限 cap = ceil(購入数 × capRate)
   *  4. STEP 3: 一次配分 = floor(購入数 × 添付率)、cap でクランプ
   *  5. STEP 4: 二次配分（ラウンドロビン） → machineFinal
   *  6. パネル候補行・pickMax・ユーザー選択の正規化
   *
   * @param {PdsAttachmentGroupRule} groupRule
   * @param {PdsCartLineForAttachment[]} allLines - 全カート行
   * @param {Record<string, Record<string, number>>|undefined} userPicksByGroup
   * @returns {PdsGroupAllocationResult}
   */
  function computeGroupAttachmentAllocation(groupRule, allLines, userPicksByGroup) {
    // ── 対象行の絞り込みと合計 N の算出 ──
    const groupLines = filterLinesForGroup(allLines, groupRule);
    const groupTotalN = sumQuantity(groupLines);
    const appliedRate = resolveRateForN(groupTotalN, groupRule);

    // ── STEP 1: 全体枠 = floor(N × 添付率) ──
    const wholeSlot = appliedRate <= 0 ? 0 : floorMul(groupTotalN, appliedRate);
    const capRate = resolveCapRate(appliedRate, groupRule);

    /** @type {Record<string, number>} */
    const primary = {};
    /** @type {Record<string, number>} */
    const caps = {};

    // ── STEP 2 & 3: 個別上限と一次配分を同時に算出 ──
    groupLines.forEach((line) => {
      const k = line.lineKey;
      const cap = ceilMul(line.quantity, capRate);              // STEP 2
      let primaryCount = floorMul(line.quantity, appliedRate);  // STEP 3
      if (primaryCount > cap) primaryCount = cap;
      caps[k] = cap;
      primary[k] = primaryCount;
    });

    // 一次配分の合計を全体枠以下にクランプ（安全弁 — 詳細は clampPrimaryToWholeSlot のコメント参照）
    const primaryClamped = clampPrimaryToWholeSlot(primary, caps, wholeSlot, groupLines);

    // ── STEP 4: 二次配分（ラウンドロビン）──
    const { allocated, tieLineKeys, tieRemaining } = distributeRemainder({
      lines: groupLines,
      primary: primaryClamped,
      caps,
      wholeSlot,
    });

    // ── 自動配分の最終結果を記録 ──
    /** @type {Record<string, number>} */
    const machineFinal = {};
    groupLines.forEach((line) => {
      machineFinal[line.lineKey] = allocated[line.lineKey] || 0;
    });

    // 一次配分（全体枠に収めたあと）が終わった時点で、各行が「個別上限 cap」まであと何個伸ばせるか。
    // 同点タイ判定（computeSymmetricTieLineKeys）や pickMax の議論で「まだ cap に余裕がある行」を表す。
    /** @type {Record<string, number>} */
    const headroomAfterPrimary = {};
    groupLines.forEach((line) => {
      const k = line.lineKey;
      headroomAfterPrimary[k] = Math.max(0, (caps[k] || 0) - (primaryClamped[k] || 0));
    });

    // ── パネル候補行の決定 ──
    // 自動配分 > 0 の行は無条件で候補。
    // 同点タイの行は自動配分 0 でも候補に含め、ユーザーが +1 できるようにする。
    /** @type {Set<string>} */
    const candidateSet = new Set();
    groupLines.forEach((line) => {
      const k = line.lineKey;
      if ((machineFinal[k] || 0) > 0) candidateSet.add(k);
    });
    if (tieLineKeys.length >= 2) {
      tieLineKeys.forEach((k) => candidateSet.add(k));
    }

    const candidateLineKeys = groupLines.map((l) => l.lineKey).filter((k) => candidateSet.has(k));

    // ── pickMax（行ごとのユーザー選択上限）の算出 ──
    //   通常行: 自動配分の個数がそのまま上限（自動結果を変えられない）
    //   タイ行: 自動配分 + タイ残枠（ただし cap を超えない）
    //
    //   例: A(10個), B(10個), C(10個), 添付率0.15, capRate0.3 の場合
    //     wholeSlot=4, cap=3, primary=1（各行）, remaining=1
    //     → ティア [A,B,C] で eligible 3行 > remaining 1 → タイ発生
    //     → machineFinal=1（一次配分のまま）, tieRemaining=1
    //     → pickMax = min(1+1, 3) = 2
    //     「自動で 1 個は確定、タイの残り 1 個をこの行に回せば最大 2 個」の意味。
    //     理論上は A,B,C 全員が pickMax まで選ぶと合計 6 > wholeSlot 4 になりうるが、
    //     パネルの ＋ は remainderLeft > 0 のときだけ有効なので通常操作では全体枠を超えられない。
    //     clampAttachmentUserPicks の全体削減（合計 > wholeSlot）は、有料数量変更などで
    //     枠が縮んだあと古い選択が残ったときの安全弁としてのみ通る。
    const tieSet = new Set(tieLineKeys);
    /** @type {Record<string, number>} */
    const pickMax = {};
    candidateLineKeys.forEach((k) => {
      if (tieSet.has(k)) {
        pickMax[k] = Math.min((machineFinal[k] || 0) + tieRemaining, caps[k] || 0);
      } else {
        pickMax[k] = machineFinal[k] || 0;
      }
    });

    // ── ユーザー選択の正規化 ──
    const rawGroupPicks = (userPicksByGroup && userPicksByGroup[groupRule.id]) || {};
    const picks = clampAttachmentUserPicks(candidateLineKeys, pickMax, wholeSlot, rawGroupPicks);
    const selectionSum = candidateLineKeys.reduce((s, k) => s + (picks[k] || 0), 0);

    // ── 候補行ごとの詳細データを組み立て ──
    /** @type {PdsGroupAllocationResult['byLine']} */
    const byLine = {};
    candidateLineKeys.forEach((k) => {
      const cap = caps[k] || 0;
      const prim = primaryClamped[k] || 0;
      const mf = machineFinal[k] || 0;
      const sel = picks[k] || 0;
      byLine[k] = {
        primary: prim,
        cap,
        headroom: headroomAfterPrimary[k] || 0,
        pickMax: pickMax[k] || 0,
        machineFinal: mf,
        selectionPick: sel,
      };
    });

    return {
      groupId: groupRule.id,
      candidateLineKeys,
      groupTotalN,
      appliedRate,
      capRate,
      wholeSlot,
      selectionPicksApplied: selectionSum,
      remainderLeft: wholeSlot - selectionSum, // ＋ボタンの有効/無効判定に使用
      machineFinal,
      byLine,
    };
  }

  /**
   * 全グループの配分をまとめて計算する。
   * 注意: 同じカート行が複数グループに属する構成は想定していない（数量が二重計上される）。
   * @param {PdsCartLineForAttachment[]} lines
   * @param {PdsAttachmentRules} rules
   * @param {Record<string, Record<string, number>>|undefined} userPicksByGroup
   * @returns {PdsGroupAllocationResult[]}
   */
  function computeAllAttachmentAllocations(lines, rules, userPicksByGroup) {
    const groupList = rules.groups || [];
    return groupList.map((g) => computeGroupAttachmentAllocation(g, lines, userPicksByGroup));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DOM → カート行データの読み取り
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * data 属性を真偽値として読む（"true" / "1" → true、それ以外 → false）。
   * @param {Element} el
   * @param {string} attrName
   * @returns {boolean}
   */
  function parseBoolDataAttr(el, attrName) {
    const v = (el.getAttribute(attrName) || '').trim().toLowerCase();
    return v === 'true' || v === '1';
  }

  /**
   * 1 つの form 内の数量 input（name="updates[]"）から添付品用のカート行データを抽出する。
   * data-pds-attachment-line-key が無い input は対象外として無視する。
   * @param {HTMLFormElement} form
   * @returns {PdsCartLineForAttachment[]}
   */
  function parseAttachmentLinesFromForm(form) {
    /** @type {PdsCartLineForAttachment[]} */
    const lines = [];
    form.querySelectorAll('.quantity__input[name="updates[]"]').forEach((input) => {
      const lineKey = input.getAttribute('data-pds-attachment-line-key');
      if (!lineKey) return;

      const attachmentProfile = (input.getAttribute('data-pds-attachment-profile') || '').trim();
      const lineTitle = (input.getAttribute('data-pds-attachment-line-title') || '').trim();
      const isGiftLine = parseBoolDataAttr(input, 'data-pds-attachment-is-gift');
      const excludeFromAttachmentCount = parseBoolDataAttr(input, 'data-pds-attachment-exclude-count');
      const attachedGiftVariantId = (input.getAttribute('data-pds-attached-gift-variant-id') || '').trim();
      const attachedGiftDisplayTitle = (input.getAttribute('data-pds-attached-gift-display-title') || '').trim();
      const attachedGiftProductTitle = (input.getAttribute('data-pds-attached-gift-product-title') || '').trim();
      const attachedGiftVariantOptions = parseAttachedGiftVariantOptionsAttr(
        input.getAttribute('data-pds-attached-gift-variant-options')
      );
      const attachedGiftImageUrl = (input.getAttribute('data-pds-attached-gift-image') || '').trim();
      const cartVariantId = (input.getAttribute('data-quantity-variant-id') || '').trim();

      const q = parseInt(input.value, 10) || 0;
      const productId = (input.getAttribute('data-pds-product-id') || '').trim();

      lines.push({
        lineKey,
        productId,
        quantity: q,
        attachmentProfile,
        lineTitle,
        isGiftLine,
        excludeFromAttachmentCount,
        attachedGiftVariantId: attachedGiftVariantId || undefined,
        attachedGiftDisplayTitle: attachedGiftDisplayTitle || undefined,
        attachedGiftProductTitle: attachedGiftProductTitle || undefined,
        attachedGiftVariantOptions:
          attachedGiftVariantOptions.length > 0 ? attachedGiftVariantOptions : undefined,
        attachedGiftImageUrl: attachedGiftImageUrl || undefined,
        cartVariantId: cartVariantId || undefined,
      });
    });
    return lines;
  }

  /**
   * 添付品計算の対象となるカートフォームが DOM 上にあるか。
   * 無いページ（トップ等）では lines が空になり得るが、カート API 同期は行わない。
   * @returns {boolean}
   */
  function hasCartAttachmentContext() {
    const cartForm = document.getElementById('cart');
    const drawerForm = document.getElementById('CartDrawer-Form');
    return (
      (cartForm && cartForm.tagName === 'FORM') ||
      (drawerForm && drawerForm.tagName === 'FORM')
    );
  }

  /**
   * #cart と #CartDrawer-Form から行データを読む。
   * カートページ（#main-cart-items あり）では #cart のみを正とする。
   * ドロワーは Section 未更新で削除済み行が残ることがあり、マージすると添付品が消えない。
   * @returns {PdsCartLineForAttachment[]}
   */
  function parseAttachmentLinesFromCartForms() {
    const cartForm = document.getElementById('cart');
    const drawerForm = document.getElementById('CartDrawer-Form');
    const mainCart = document.getElementById('main-cart-items');

    if (mainCart && cartForm && cartForm.tagName === 'FORM') {
      return parseAttachmentLinesFromForm(cartForm);
    }

    /** @type {Map<string, PdsCartLineForAttachment>} */
    const byLineKey = new Map();
    const mergeForm = (form) => {
      parseAttachmentLinesFromForm(form).forEach((line) => {
        const existing = byLineKey.get(line.lineKey);
        if (!existing) {
          byLineKey.set(line.lineKey, { ...line });
        } else {
          existing.quantity = line.quantity;
          if (line.lineTitle) existing.lineTitle = line.lineTitle;
          if (line.attachedGiftVariantId) existing.attachedGiftVariantId = line.attachedGiftVariantId;
          if (line.attachedGiftDisplayTitle) existing.attachedGiftDisplayTitle = line.attachedGiftDisplayTitle;
        }
      });
    };

    if (drawerForm && drawerForm.tagName === 'FORM') mergeForm(drawerForm);
    if (cartForm && cartForm.tagName === 'FORM') mergeForm(cartForm);

    return Array.from(byLineKey.values());
  }

  /**
   * Cart API の行が有料商品か（_pds_attached_gift マーカー付きは false）。
   * @param {object} item
   * @returns {boolean}
   */
  function isCartPaidLineItem(item) {
    const props = item && item.properties;
    const marker = props && (props._pds_attached_gift === '1' || props._pds_attached_gift === 'true');
    return !marker;
  }

  /**
   * DOM から読んだ行のうち、Cart API にまだ存在する有料行だけを残す。
   * @param {PdsCartLineForAttachment[]} lines
   * @param {{ items?: { key: string }[] }} [cart] - 省略時はそのまま返す
   * @returns {PdsCartLineForAttachment[]}
   */
  function filterLinesToCurrentCartPaidItems(lines, cart) {
    if (!lines.length || !cart || !Array.isArray(cart.items)) return lines;
    const paidKeys = new Set(cart.items.filter(isCartPaidLineItem).map((item) => item.key));
    return lines.filter((l) => paidKeys.has(l.lineKey));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 公開 API: ユーザー選択と計算の実行
  // ─────────────────────────────────────────────────────────────────────────

  // ユーザーの選択状態: { グループID: { 行キー: 個数 } }
  // ページ間の永続化はテーマ（セッションストレージ等）が担当
  if (typeof window.pdsAttachmentUserPicks !== 'object' || window.pdsAttachmentUserPicks === null) {
    window.pdsAttachmentUserPicks = {};
  }

  /**
   * 添付品の配分を計算して返す（公開 API）。
   * @param {PdsCartLineForAttachment[]} [lines] - 省略時はフォームから読む
   * @param {PdsAttachmentRules} [rules] - 省略時は window.pdsAttachmentRules
   * @param {Record<string, Record<string, number>>} [userPicksOverride] - 一時上書き用
   * @returns {PdsGroupAllocationResult[]} ルール無効時は空配列
   */
  window.pdsComputeAttachmentAllocations = function (lines, rules, userPicksOverride) {
    const r = rules || getRules();
    if (!r || r.enabled === false || !r.groups || !r.groups.length) {
      return [];
    }
    const sourceLines = lines && lines.length ? lines : parseAttachmentLinesFromCartForms();
    const merged = mergeUserPicks(window.pdsAttachmentUserPicks, userPicksOverride);
    return computeAllAttachmentAllocations(sourceLines, r, merged);
  };

  /**
   * 引数なし版。フォームから自動でデータを読んで計算する。
   * @returns {PdsGroupAllocationResult[]}
   */
  window.pdsGetAttachmentAllocationsFromForms = function () {
    return window.pdsComputeAttachmentAllocations();
  };

  /**
   * ± ボタンで選択個数を増減する（公開 API）。
   * 選択を更新後、配分を再計算しパネルを再描画してカートに反映する。
   * @param {string} groupId
   * @param {string} lineKey
   * @param {number} delta - 通常 ±1
   */
  window.pdsAttachmentAdjustPick = function (groupId, lineKey, delta) {
    window.pdsAttachmentUserPicks = window.pdsAttachmentUserPicks || {};
    // このグループ・この行の現在の選択個数を取得（未選択なら 0）
    const cur = window.pdsAttachmentUserPicks[groupId]?.[lineKey] || 0;
    // グループの既存選択を保持しつつ、対象行だけ delta 分を加減した値で上書きする
    // 例: delta=+1 → cur が 2 なら 3 に、delta=-1 → cur が 2 なら 1 に（0 未満にはしない）
    window.pdsAttachmentUserPicks[groupId] = {
      ...(window.pdsAttachmentUserPicks[groupId] || {}),
      [lineKey]: Math.max(0, cur + delta),
    };
    recomputeAndSyncAttachmentGifts();
  };

  /**
   * 添付品の対象となる有料行が無いときはユーザー選択をクリアする。
   * @param {PdsCartLineForAttachment[]} lines
   */
  function clearAttachmentStateIfNoEligibleLines(lines) {
    // カートフォームが無いページ（トップ等）では lines が空になり得る。ここでクリアすると
    // 直後の sync がカート内の添付品を 0 にしてしまうため触らない。
    if (!lines.length) return;
    const hasEligible = lines.some((l) => !l.isGiftLine && l.attachedGiftVariantId);
    if (!hasEligible) {
      window.pdsAttachmentUserPicks = {};
    }
  }

  /**
   * 添付品の配分を一括で再計算し、UI と Shopify カートを同期するメイン経路。
   *
   * ± ボタン（pdsAttachmentAdjustPick）、DOMContentLoaded 後の初期化、
   * cartUpdate（通常商品の数量変更・削除など）から呼ばれる。
   *
   * 処理の流れ:
   *  1. ルール無効時はパネルだけ空描画して終了
   *  2. カートフォームから有料行を読み取り、対象行が無ければ選択状態をクリア
   *  3. 全体枠・pickMax・ユーザー選択を再計算（clamp 含む）
   *  4. 正規化後の選択を window.pdsAttachmentUserPicks に書き戻す
   *  5. 添付品パネルを再描画（＋の disabled もここで更新）
   *  6. Cart API で添付品バリアントの数量を差分同期し、必要ならカート表を再取得
   *
   * @param {{ cartSnapshot?: { items?: object[] } }} [options] - cart-items の cartUpdate 等で渡す直近のカート JSON
   * @returns {Promise<void>}
   */
  async function recomputeAndSyncAttachmentGifts(options = {}) {
    if (_recomputeInFlight) {
      _pendingRecompute = true;
      return;
    }
    _recomputeInFlight = true;

    try {
      const r = getRules();
      // 機能オフ時は計算・カート反映を行わず、パネルだけ非表示にする
      if (!r || r.enabled === false || !r.groups || !r.groups.length) {
        renderAttachmentSelectionPanel();
        return;
      }

      let lines = parseAttachmentLinesFromCartForms();
      const needsCartApi = hasCartAttachmentContext();
      /** @type {{ items?: object[] } | null} */
      let cartSnapshot = options.cartSnapshot || null;

      if (needsCartApi && !cartSnapshot) {
        try {
          cartSnapshot = await fetchCartJson(3, { maxAge: 0 });
        } catch (err) {
          console.error('[pds-attached-gifts] カート取得に失敗:', err);
          _attachmentCartSyncFailed = true;
          return;
        }
      }

      if (needsCartApi && cartSnapshot) {
        lines = filterLinesToCurrentCartPaidItems(lines, cartSnapshot);
      }

      // 有料行が無い（全削除・添付品のみ残存）ときも孤立添付品を Cart API で除去する。
      // カートフォームが無いページ（トップ等）では同期しない。
      if (!lines.length) {
        renderAttachmentSelectionPanel();
        if (!needsCartApi) return;
        window.pdsAttachmentUserPicks = {};
        await syncAttachmentGiftsToCart([], []);
        await refreshCartItemsSectionContents();
        return;
      }

      clearAttachmentStateIfNoEligibleLines(lines);

      const results = computeAllAttachmentAllocations(lines, r, mergeUserPicks(window.pdsAttachmentUserPicks, {}));
      syncStoredPicksFromAllocation(results);
      renderAttachmentSelectionPanel();
      // sync 判定は常に最新 cart.js を取り直す（options.cartSnapshot は有料行 filter 専用）
      await syncAttachmentGiftsToCart(results, lines);
    } finally {
      _recomputeInFlight = false;
      if (_pendingRecompute) {
        _pendingRecompute = false;
        // 直前の sync（add/update）でカートが変わっていることがある。
        // 空 options で取り直し、stale snapshot による二重 add を防ぐ。
        await recomputeAndSyncAttachmentGifts({});
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // カート反映: Shopify Cart API で添付品バリアントの数量を同期
  // ─────────────────────────────────────────────────────────────────────────

  /** 再計算の直列化（filter + sync の二重 GET を防ぐ） */
  let _recomputeInFlight = false;
  let _pendingRecompute = false;
  /** cartUpdate 連打時のデバウンス */
  let _recomputeAfterCartUpdateTimer = null;
  /** @type {{ items?: object[] } | null} */
  let _recomputeAfterCartUpdateCartSnapshot = null;

  /** 二重送信防止フラグ */
  let _syncInFlight = false;
  /** 通信中に新しいリクエストが来たら控えておく（最新のみ保持） */
  /** @type {{ results: PdsGroupAllocationResult[], lines: PdsCartLineForAttachment[] } | null} */
  let _pendingSync = null;
  /**
   * 直近の添付品カート同期（または同期前の cart 取得）が失敗したか。
   * チェックアウト reconcile 前に false へ戻し、同期成功時も false に戻す。
   * 失敗のまま settle した場合は遷移させない。
   */
  let _attachmentCartSyncFailed = false;
  /** チェックアウト遷移のガード（多重submitを防ぐ） */
  let _checkoutSubmitGuard = false;
  /**
   * チェックアウト直前の添付品最終同期を済ませたあとの再 submit かどうか。
   * true のときだけ同期待ちをスキップして遷移を許可し、直後に false へ戻す。
   */
  let _checkoutAttachmentReconciled = false;
  /** 初回 restorePicksFromCart 完了フラグ（未完了時の誤判定防止） */
  let _picksRestored = false;
  /** @type {Promise<void>} */
  let _picksRestorePromise = Promise.resolve();

  const ATTACHMENT_CHECKOUT_NOTICE_IDS = [
    'pds-cart-attachment-notice',
    'pds-cart-drawer-attachment-notice',
  ];

  /**
   * 全体枠に対して、UI 上まだ選べる選択が足りないグループがあるか判定する。
   * remainderLeft だけでなく pickMax 余裕も見る（cap 合計 < wholeSlot などで達成不能な残りはブロックしない）。
   * @returns {{ remaining: number } | null} 不足があるとき残り個数、なければ null
   */
  function getIncompleteAttachmentSelection() {
    const r = getRules();
    if (!r || r.enabled === false || !r.groups || !r.groups.length) return null;

    const results = window.pdsGetAttachmentAllocationsFromForms();
    let remaining = 0;
    results.forEach((g) => {
      const keys = g.candidateLineKeys || Object.keys(g.byLine || {});
      if (!(g.wholeSlot > 0 && keys.length > 0 && g.remainderLeft > 0)) return;

      // パネルの＋と同じ: pickMax に余裕がある行だけを「選べる」とみなす
      let pickableHeadroom = 0;
      keys.forEach((k) => {
        const row = g.byLine && g.byLine[k];
        if (!row) return;
        pickableHeadroom += Math.max(0, (row.pickMax || 0) - (row.selectionPick || 0));
      });
      const actionable = Math.min(g.remainderLeft, pickableHeadroom);
      if (actionable > 0) remaining += actionable;
    });
    return remaining > 0 ? { remaining } : null;
  }

  /**
   * 未選択エラー文言を組み立てる。
   * @param {number} remaining
   * @returns {string}
   */
  function buildIncompleteAttachmentMessage(remaining) {
    const r = getRules();
    const template =
      (r && r.strings && r.strings.incompleteSelection) ||
      '添付品の選択が完了していません。あと[remaining]個お選びください。';
    return template.replace(/\[remaining\]/g, String(remaining));
  }

  /**
   * 添付品カート同期失敗時のチェックアウトエラー文言。
   * @returns {string}
   */
  function buildAttachmentSyncFailedMessage() {
    const r = getRules();
    return (
      (r && r.strings && r.strings.syncFailed) ||
      '添付品の反映に失敗しました。時間をおいて、もう一度お試しください。'
    );
  }

  /**
   * カート／ドロワーの添付品専用 notice 要素を返す（数量ルール用 notice とは分離）。
   * @param {HTMLFormElement} [form]
   * @returns {HTMLElement|null}
   */
  function getAttachmentCheckoutNoticeEl(form) {
    const formId = form && form.getAttribute('id');
    if (formId === 'CartDrawer-Form') {
      return document.getElementById('pds-cart-drawer-attachment-notice');
    }
    if (formId === 'cart') {
      return document.getElementById('pds-cart-attachment-notice');
    }
    return (
      document.getElementById('pds-cart-attachment-notice') ||
      document.getElementById('pds-cart-drawer-attachment-notice')
    );
  }

  /**
   * 数量ルールの notice が表示中か（チェックアウトボタン併用判定用）。
   * @param {string} noticeId
   * @returns {boolean}
   */
  function isQuantityNoticeBlocking(noticeId) {
    const el = document.getElementById(noticeId);
    return Boolean(el && !el.hidden && el.textContent.trim());
  }

  /**
   * 添付品未選択に応じてチェックアウトボタンを非活性にする（最低数量制限と同じ disabled 見た目）。
   * @param {boolean} blockedByAttachment
   */
  function updateAttachmentCheckoutButtons(blockedByAttachment) {
    const targets = [
      { btnId: 'checkout', quantityNoticeId: 'pds-cart-quantity-notice' },
      { btnId: 'CartDrawer-Checkout', quantityNoticeId: 'pds-cart-drawer-quantity-notice' },
    ];
    targets.forEach(({ btnId, quantityNoticeId }) => {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      if (blockedByAttachment) {
        btn.setAttribute('data-pds-attachment-blocked', '');
      } else {
        btn.removeAttribute('data-pds-attachment-blocked');
      }
      btn.disabled =
        blockedByAttachment ||
        isQuantityNoticeBlocking(quantityNoticeId) ||
        btn.hasAttribute('data-pds-checkout-locked');
    });
  }

  /**
   * 添付品未選択エラーを notice に表示し、チェックアウトを止める。
   * @param {HTMLFormElement} form
   * @param {string} message
   * @param {{ focus?: boolean }} [opts]
   */
  function showAttachmentCheckoutError(form, message, opts) {
    const shouldFocus = !opts || opts.focus !== false;
    const noticeEl = getAttachmentCheckoutNoticeEl(form);
    if (noticeEl) {
      noticeEl.textContent = message;
      noticeEl.hidden = false;
      if (shouldFocus && typeof noticeEl.focus === 'function') {
        noticeEl.focus();
      }
    }
    // カート／ドロワー両方の notice を同期（Section 差し替え後の片側欠落を防ぐ）
    ATTACHMENT_CHECKOUT_NOTICE_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el || el === noticeEl) return;
      el.textContent = message;
      el.hidden = false;
    });
    updateAttachmentCheckoutButtons(true);

    if (!shouldFocus) return;
    const panel = document.querySelector('.pds-attached-gifts-root:not([hidden])');
    if (panel && typeof panel.scrollIntoView === 'function') {
      panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * 添付品未選択エラーを消し、ボタン非活性を解除する。
   */
  function clearAttachmentCheckoutError() {
    ATTACHMENT_CHECKOUT_NOTICE_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    });
    updateAttachmentCheckoutButtons(false);
  }

  /**
   * 未選択なら notice 表示＋ボタン非活性、完了なら解除（最低数量制限と同じ事前ガード）。
   * 選択復元前は誤判定を避けるため何もしない。
   */
  function syncAttachmentCheckoutErrorWithSelection() {
    if (!_picksRestored) return;

    const incomplete = getIncompleteAttachmentSelection();
    if (!incomplete) {
      clearAttachmentCheckoutError();
      return;
    }
    const message = buildIncompleteAttachmentMessage(incomplete.remaining);
    ATTACHMENT_CHECKOUT_NOTICE_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    });
    updateAttachmentCheckoutButtons(true);
  }

  /**
   * Section 差し替え後も有効なカートフォームを返す。
   * @param {HTMLFormElement} form
   * @returns {HTMLFormElement|null}
   */
  function resolveLiveCartForm(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    if (form.id) {
      const live = document.getElementById(form.id);
      if (live instanceof HTMLFormElement) return live;
    }
    return form.isConnected ? form : null;
  }

  /**
   * チェックアウト用 submitter を現在の DOM から取得する。
   * ボタンは form 属性でフォーム外に置かれていることがある。
   * @param {HTMLFormElement} form
   * @param {HTMLElement|null|undefined} preferred
   * @returns {HTMLElement|null}
   */
  function resolveCheckoutSubmitter(form, preferred) {
    if (!(form instanceof HTMLFormElement)) return null;

    /** @type {HTMLElement[]} */
    const candidates = [];
    if (preferred instanceof HTMLElement) {
      const byId = preferred.id ? document.getElementById(preferred.id) : null;
      if (byId instanceof HTMLElement) candidates.push(byId);
      else if (preferred.isConnected) candidates.push(preferred);
    }

    // カート／ドロワーの既知 ID（Section 差し替え後も安定）
    const knownId =
      form.id === 'CartDrawer-Form' ? 'CartDrawer-Checkout' : form.id === 'cart' ? 'checkout' : null;
    if (knownId) {
      const known = document.getElementById(knownId);
      if (known instanceof HTMLElement) candidates.push(known);
    }

    if (form.id) {
      const byFormAttr = document.querySelector(
        'button[type="submit"][name="checkout"][form="' +
          form.id +
          '"], input[type="submit"][name="checkout"][form="' +
          form.id +
          '"]'
      );
      if (byFormAttr instanceof HTMLElement) candidates.push(byFormAttr);
    }

    const nested = form.querySelector(
      'button[type="submit"][name="checkout"], input[type="submit"][name="checkout"], #checkout, #CartDrawer-Checkout'
    );
    if (nested instanceof HTMLElement) candidates.push(nested);

    for (let i = 0; i < candidates.length; i++) {
      const el = candidates[i];
      // requestSubmit(submitter) は form owner 一致が必須
      if (el.form === form) return el;
    }
    return null;
  }

  /**
   * フォールバック用の永続 checkout submitter を返す（都度削除しない）。
   * b2b-delivery-date.js が非同期保存後に同じ submitter で requestSubmit するため、
   * 一時生成して finally で消すと再送が失敗する。
   * @param {HTMLFormElement} form
   * @returns {HTMLButtonElement}
   */
  function getOrCreateCheckoutIntentSubmitter(form) {
    const existing = form.querySelector('button[data-pds-checkout-intent="1"]');
    if (existing instanceof HTMLButtonElement) return existing;

    const tempSubmitter = document.createElement('button');
    tempSubmitter.type = 'submit';
    tempSubmitter.name = 'checkout';
    tempSubmitter.hidden = true;
    tempSubmitter.setAttribute('data-pds-checkout-intent', '1');
    form.appendChild(tempSubmitter);
    return tempSubmitter;
  }

  /**
   * 非同期ガード後にチェックアウトを再開する（submitter 解決失敗時などのフォールバック）。
   * 実ボタンを優先し、無ければ永続 intent ボタンで requestSubmit/click する。
   * form.submit() は使わない（配送日時・数量ルール等の submit リスナーを迂回するため）。
   * @param {HTMLFormElement} form
   */
  function resumeCheckoutSubmit(form) {
    const incomplete = getIncompleteAttachmentSelection();
    if (incomplete) {
      showAttachmentCheckoutError(form, buildIncompleteAttachmentMessage(incomplete.remaining));
      return;
    }
    clearAttachmentCheckoutError();

    const liveSubmitter = resolveCheckoutSubmitter(form, null);
    if (liveSubmitter && typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit(liveSubmitter);
        return;
      } catch (_) {
        // form owner 不一致などは intent ボタンへ
      }
    }

    const intentSubmitter = getOrCreateCheckoutIntentSubmitter(form);
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(intentSubmitter);
        return;
      }
      // click は submit イベントを発火する（native form.submit() は発火しない）
      intentSubmitter.click();
    } catch (err) {
      console.error('[pds-attached-gifts] チェックアウト再開に失敗:', err);
    }
  }

  /**
   * 同期待ち／復元待ちのあと、submit イベント付きでチェックアウトをやり直す。
   * requestSubmit なら数量ルール等の他リスナーも再実行される。
   * 添付品同期の Section 差し替えでイベント時の form/submitter が古くなるため、都度 DOM から取り直す。
   * @param {HTMLFormElement} form
   * @param {HTMLElement|null|undefined} submitter
   * @param {number} [attempt]
   */
  function retryCheckoutSubmit(form, submitter, attempt) {
    const tryCount = typeof attempt === 'number' ? attempt : 0;
    const liveForm = resolveLiveCartForm(form);
    if (!liveForm) {
      // Section 差し替え直後は一瞬 form が無いことがあるため短くリトライする
      if (tryCount < 5) {
        setTimeout(() => retryCheckoutSubmit(form, submitter, tryCount + 1), 100);
        return;
      }
      showAttachmentCheckoutError(
        form,
        'チェックアウトを再開できませんでした。もう一度お試しください。',
        { focus: true }
      );
      return;
    }

    const liveSubmitter = resolveCheckoutSubmitter(liveForm, submitter);

    if (typeof liveForm.requestSubmit === 'function') {
      try {
        if (liveSubmitter) {
          liveForm.requestSubmit(liveSubmitter);
          return;
        }
      } catch (_) {
        // form owner 不一致などで失敗したら下のフォールバックへ
      }
    }
    resumeCheckoutSubmit(liveForm);
  }

  /**
   * @param {number} [maxAttempts]
   * @param {{ maxAge?: number }} [opts]
   * @returns {Promise<{ items: object[], item_count?: number }>}
   */
  function fetchCartJson(maxAttempts = 3, opts = {}) {
    if (typeof window.pdsFetchCartJson === 'function') {
      return window.pdsFetchCartJson({ maxAttempts, ...opts });
    }
    return fetch(window.routes.cart_url + '.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }).then((resp) => {
      if (!resp.ok) throw new Error('Cart GET failed: ' + resp.status);
      return resp.json();
    });
  }

  /**
   * cart/add.js・cart/update.js（fetchConfig 形式・429 リトライ）。
   * @param {string} url
   * @param {object} body
   * @returns {Promise<Response>}
   */
  function fetchCartMutation(url, body) {
    if (typeof window.pdsPostCartJson === 'function') {
      const cfg = typeof fetchConfig === 'function' ? fetchConfig() : {};
      return window.pdsPostCartJson(url, body, { headers: cfg.headers });
    }
    const config = { ...fetchConfig(), body: JSON.stringify(body) };
    return fetch(url, config);
  }

  /**
   * カート API の行が添付品行かどうか（有料行のバリアント ID は常に false）。
   * @param {object} item - cart.js の line item
   * @param {Set<string>} activeGiftVariantIds - 現在フォーム上の有料行が参照する添付品バリアント ID
   * @param {Set<string>} paidVariantIds - フォーム上の有料行バリアント ID
   * @returns {boolean}
   */
  function isAttachedGiftCartItem(item, activeGiftVariantIds, paidVariantIds) {
    const vid = String(item.variant_id);
    if (paidVariantIds.has(vid)) return false;
    const props = item && item.properties;
    const marker = props && (props._pds_attached_gift === '1' || props._pds_attached_gift === 'true');
    if (marker) return true;
    return activeGiftVariantIds.has(vid);
  }

  /**
   * Section Rendering API の HTML から selector 配下の innerHTML を取り出す（cart.js と同様）。
   * @param {string} html
   * @param {string} selector
   * @returns {string}
   */
  function getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * cart.js の item_count をヘッダー #cart-icon-bubble のバッジに反映する（軽量）。
   * .cart-count-bubble が無いときだけ Section API にフォールバックする。
   * @param {{ item_count?: number }} cart
   * @returns {Promise<void>}
   */
  async function applyCartIconBubbleFromCart(cart) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !cart) return;

    const count = Math.max(0, Number(cart.item_count) || 0);
    const countBubble = bubble.querySelector('.cart-count-bubble');

    if (count === 0) {
      countBubble?.remove();
      return;
    }

    if (countBubble) {
      const visible = countBubble.querySelector('[aria-hidden="true"]');
      if (visible) visible.textContent = count < 100 ? String(count) : '';
      return;
    }

    const cartUrl = window.routes?.cart_url || '/cart';
    try {
      const resp = await fetch(`${cartUrl}?section_id=cart-icon-bubble`);
      if (!resp.ok) return;
      bubble.innerHTML = getSectionInnerHTML(await resp.text(), '.shopify-section');
    } catch (err) {
      console.error('[pds-attached-gifts] カートアイコンの更新に失敗:', err);
    }
  }

  /**
   * 配送日 UI の document リスナーを外してから DOM から除去する。
   * @param {Element|null} deliveryRoot
   */
  function removeDeliveryDateRoot(deliveryRoot) {
    if (!deliveryRoot) return;
    if (typeof window.PdsDeliveryDate?.resetRoot === 'function') {
      window.PdsDeliveryDate.resetRoot(deliveryRoot);
    }
    deliveryRoot.remove();
  }

  /**
   * @param {Element} footer
   * @returns {Element|null}
   */
  function getCheckoutBlocksContainer(footer) {
    const blocks = footer.querySelectorAll('.cart__blocks');
    if (blocks.length > 1) return blocks[blocks.length - 1];
    return blocks[0] || null;
  }

  /**
   * @param {Element} footer
   * @param {Element} node
   */
  function insertDeliveryDateNode(footer, node) {
    const checkoutCta = footer.querySelector('.cart__blocks .cart__ctas');
    if (checkoutCta) {
      checkoutCta.before(node);
      return;
    }

    const checkoutBlocks = getCheckoutBlocksContainer(footer);
    if (checkoutBlocks) {
      checkoutBlocks.appendChild(node);
      return;
    }

    footer.appendChild(node);
  }

  /**
   * 配送日 UI の表示/非表示と拠点・leadtime 変更時の差し替え。
   * @param {Element} curFooter
   * @param {Element} newFooter
   * @returns {boolean} 配送日 DOM を追加または差し替えたか
   */
  function syncCartFooterDeliveryDate(curFooter, newFooter) {
    const curDelivery = curFooter.querySelector('pds-delivery-date');
    const newDelivery = newFooter.querySelector('pds-delivery-date');

    if (curDelivery && !newDelivery) {
      removeDeliveryDateRoot(curDelivery);
      return false;
    }

    if (!curDelivery && newDelivery) {
      insertDeliveryDateNode(curFooter, document.importNode(newDelivery, true));
      return true;
    }

    if (curDelivery && newDelivery) {
      const pageLocationId =
        document.getElementById('cart-attributes-cart_company_location_id')?.value?.trim() || '';
      const curLocationId = curDelivery.dataset.locationId || '';
      const locationMismatch = Boolean(
        pageLocationId && curLocationId && pageLocationId !== curLocationId
      );

      const getContext = window.PdsDeliveryDate?.getContextFromRoot;
      const contextsDiffer = window.PdsDeliveryDate?.contextsDiffer;
      const curContext = typeof getContext === 'function' ? getContext(curDelivery) : null;
      const newContext = typeof getContext === 'function' ? getContext(newDelivery) : null;
      const contextMismatch =
        typeof contextsDiffer === 'function' && contextsDiffer(curContext, newContext);
      const preferSectionContext =
        !pageLocationId ||
        !newContext?.locationId ||
        newContext.locationId === pageLocationId;

      if (locationMismatch || contextMismatch) {
        if (locationMismatch || preferSectionContext) {
          removeDeliveryDateRoot(curDelivery);
          const imported = document.importNode(newDelivery, true);
          imported.dataset.pdsDeliverySkipConnectInit = 'true';
          insertDeliveryDateNode(curFooter, imported);
          return true;
        }
      }
    }

    return false;
  }

  /**
   * main-cart-footer セクションを Section API で差し替える（合計・チェックアウト）。
   * 配送日 UI は .js-contents 外のため部分更新し、ユーザー選択を維持する。
   * @param {Document} [footerDoc] - 取得済み HTML（省略時は Section API で取得）
   * @returns {Promise<void>}
   */
  async function refreshCartFooterSectionContents(footerDoc) {
    const curFooter = document.getElementById('main-cart-footer');
    if (!curFooter?.dataset?.id) return;

    try {
      let doc = footerDoc;
      if (!doc) {
        const cartUrl = window.routes?.cart_url || '/cart';
        const resp = await fetch(`${cartUrl}?section_id=${curFooter.dataset.id}&_=${Date.now()}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!resp.ok) return;
        doc = new DOMParser().parseFromString(await resp.text(), 'text/html');
      }

      const newFooter = doc.getElementById('main-cart-footer');
      if (!newFooter) return;

      const curBlocksList = curFooter.querySelectorAll('.cart__blocks');
      const newBlocksList = newFooter.querySelectorAll('.cart__blocks');

      if (curBlocksList[0] && newBlocksList[0]) {
        const curContents = curBlocksList[0].querySelector('.js-contents');
        const newContents = newBlocksList[0].querySelector('.js-contents');
        if (curContents && newContents) {
          curContents.innerHTML = newContents.innerHTML;
        }
      }

      const deliveryInserted = syncCartFooterDeliveryDate(curFooter, newFooter);

      const curCheckoutBlocks = getCheckoutBlocksContainer(curFooter);
      const newCheckoutBlocks = getCheckoutBlocksContainer(newFooter);
      let ctasReplaced = false;
      if (curCheckoutBlocks && newCheckoutBlocks) {
        const curCtas = curCheckoutBlocks.querySelector('.cart__ctas');
        const newCtas = newCheckoutBlocks.querySelector('.cart__ctas');
        if (curCtas && newCtas) {
          curCtas.replaceWith(document.importNode(newCtas, true));
          ctasReplaced = true;
        } else if (!curCtas && newCtas) {
          curCheckoutBlocks.prepend(document.importNode(newCtas, true));
          ctasReplaced = true;
        } else if (curCtas && !newCtas) {
          curCtas.remove();
          ctasReplaced = true;
        }

        const curErrors = curCheckoutBlocks.querySelector('#cart-errors');
        const newErrors = newCheckoutBlocks.querySelector('#cart-errors');
        if (curErrors && newErrors) {
          curErrors.innerHTML = newErrors.innerHTML;
        }
      }

      // CTA 差し替えで data-pds-attachment-blocked が消えるため、未選択ガードをすぐ付け直す
      if (ctasReplaced) {
        syncAttachmentCheckoutErrorWithSelection();
      }

      curFooter.classList.toggle('is-empty', newFooter.classList.contains('is-empty'));

      const curDelivery = curFooter.querySelector('pds-delivery-date');
      const pageLocationId =
        document.getElementById('cart-attributes-cart_company_location_id')?.value?.trim() || '';
      const locationStillMismatch = Boolean(
        curDelivery &&
          pageLocationId &&
          curDelivery.dataset.locationId &&
          curDelivery.dataset.locationId !== pageLocationId
      );

      if (
        (deliveryInserted || locationStillMismatch) &&
        typeof window.PdsDeliveryDate?.reload === 'function'
      ) {
        window.PdsDeliveryDate.reload();
      }
    } catch (err) {
      console.error('[pds-attached-gifts] カートフッターの再取得に失敗:', err);
    }
  }

  /**
   * カートページ（main-cart-items）の有料商品表・フッターを Section API で差し替える。
   * 添付品パネル・配送日 UI は .js-contents 外のため innerHTML 差し替えで消えない。
   *
   * @returns {Promise<void>}
   */
  async function refreshMainCartPageSectionContents() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const main = document.getElementById('main-cart-items');
    if (!main?.dataset?.id) return;

    const footerEl = document.getElementById('main-cart-footer');
    const sectionIds = [main.dataset.id];
    if (footerEl?.dataset?.id) {
      sectionIds.push(footerEl.dataset.id);
    }

    const responses = await Promise.all(
      sectionIds.map((id) =>
        fetch(`${cartUrl}?section_id=${id}&_=${Date.now()}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        })
      )
    );
    if (!responses[0].ok) return;

    const itemsDoc = new DOMParser().parseFromString(await responses[0].text(), 'text/html');

    const curItems = document.querySelector('cart-items');
    const newItems = itemsDoc.querySelector('cart-items');
    if (curItems && newItems) {
      const curContents = curItems.querySelector('.js-contents');
      const newContents = newItems.querySelector('.js-contents');
      if (curContents && newContents) {
        curContents.innerHTML = newContents.innerHTML;
      }
      curItems.classList.toggle('is-empty', newItems.classList.contains('is-empty'));
    }

    if (responses[1]?.ok && footerEl) {
      const footerDoc = new DOMParser().parseFromString(await responses[1].text(), 'text/html');
      await refreshCartFooterSectionContents(footerDoc);
    } else if (curItems?.classList.contains('is-empty') && footerEl) {
      footerEl.classList.add('is-empty');
    }
  }

  /**
   * カートドロワーの行・フッターを Section API で差し替える（data-index のずれ防止）。
   * 添付品パネルは #CartDrawer-CartItems 内にあるため、表部分だけ更新するか、
   * 空↔非空の切り替え時のみ Dawn 同様に cart-drawer-items ごと差し替える。
   * 添付品 notice は .cart-drawer__footer 外のため、フッター差し替えでも消えない。
   *
   * @returns {Promise<void>}
   */
  async function refreshCartDrawerSectionContents() {
    const curDrawerItems = document.querySelector('cart-drawer-items');
    if (!curDrawerItems) return;

    const cartUrl = window.routes?.cart_url || '/cart';
    const resp = await fetch(`${cartUrl}?section_id=cart-drawer`);
    if (!resp.ok) return;

    const doc = new DOMParser().parseFromString(await resp.text(), 'text/html');
    const newDrawerItems = doc.querySelector('cart-drawer-items');
    if (!newDrawerItems) return;

    const curIsEmpty = curDrawerItems.classList.contains('is-empty');
    const newIsEmpty = newDrawerItems.classList.contains('is-empty');

    if (curIsEmpty !== newIsEmpty) {
      for (const selector of ['cart-drawer-items', '.cart-drawer__footer']) {
        const target = document.querySelector(selector);
        const source = doc.querySelector(selector);
        if (target && source) {
          target.replaceWith(source);
        }
      }
      const curDrawer = document.querySelector('cart-drawer');
      const newDrawer = doc.querySelector('cart-drawer');
      if (curDrawer && newDrawer) {
        curDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }
      return;
    }

    const curCartItems = document.getElementById('CartDrawer-CartItems');
    const newCartItems = doc.getElementById('CartDrawer-CartItems');
    if (curCartItems && newCartItems) {
      const curWrapper = curCartItems.querySelector('.drawer__cart-items-wrapper');
      const newWrapper = newCartItems.querySelector('.drawer__cart-items-wrapper');
      if (curWrapper && newWrapper) {
        curWrapper.innerHTML = newWrapper.innerHTML;
      }
    }

    const curFooter = document.querySelector('.cart-drawer__footer');
    const newFooter = doc.querySelector('.cart-drawer__footer');
    if (curFooter && newFooter) {
      curFooter.innerHTML = newFooter.innerHTML;
    }

    curDrawerItems.className = newDrawerItems.className;
  }

  /**
   * 添付品同期後、カートページ・ドロワーの有料行表示を Section API で揃える。
   *
   * @returns {Promise<void>}
   */
  async function refreshCartItemsSectionContents() {
    try {
      await Promise.all([refreshMainCartPageSectionContents(), refreshCartDrawerSectionContents()]);
    } catch (err) {
      console.error('[pds-attached-gifts] カート表示の再取得に失敗:', err);
    } finally {
      // ドロワーフッター等の差し替えで notice が消えるため、表示中なら復元する
      syncAttachmentCheckoutErrorWithSelection();
    }
  }

  /**
   * 再計算・カート同期のいずれかが進行中（または保留中）か。
   * @returns {boolean}
   */
  function isAttachmentWorkBusy() {
    return Boolean(_recomputeInFlight || _pendingRecompute || _syncInFlight || _pendingSync);
  }

  /**
   * settle タイムアウト後も作業が残っている場合は同期失敗として扱う。
   * （失敗フラグ未設定のままチェックアウトへ進むのを防ぐ）
   */
  function markAttachmentBusyTimeoutAsFailed() {
    if (isAttachmentWorkBusy()) {
      _attachmentCartSyncFailed = true;
    }
  }

  /**
   * 再計算（pending 含む）とカート同期が落ち着くまで待つ。
   * 初期化で _picksRestored を立てる前に使い、並行 cartUpdate とのレースを防ぐ。
   * @param {number} [timeoutMs]
   * @returns {Promise<void>}
   */
  function waitForAttachmentWorkToSettle(timeoutMs = 10000) {
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        if (!isAttachmentWorkBusy()) {
          resolve();
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(tick, 50);
      };
      tick();
    });
  }

  /**
   * 計算結果に基づき、添付品バリアントをカートへ追加/更新/削除する。
   * 差分だけを API に送信し、変更がなければ API を呼ばない。
   * 通信中に呼ばれた場合は最新のリクエストを控え、完了後に再実行する。
   *
   * 重要: add/update 判定は必ず最新の cart.js を取り直す。
   * cartUpdate 由来の古いスナップショットを使うと、既に追加済みの添付品を見逃して
   * cart/add が二重実行され、数量が 2 倍になる（パネルは 1・チェックアウトは 2）。
   *
   * @param {PdsGroupAllocationResult[]} results
   * @param {PdsCartLineForAttachment[]} lines
   * @returns {Promise<void>}
   */
  async function syncAttachmentGiftsToCart(results, lines) {
    if (_syncInFlight) {
      // 保留分は常に完了後に最新 cart で再判定する
      _pendingSync = { results, lines };
      return;
    }
    _syncInFlight = true;

    try {
      // 1) lineKey → 添付品バリアント ID の対応表
      /** @type {Record<string, string>} */
      const giftVariantByLineKey = {};
      lines.forEach((l) => {
        if (l.attachedGiftVariantId) {
          giftVariantByLineKey[l.lineKey] = l.attachedGiftVariantId;
        }
      });

      const activeGiftVariantIds = new Set(Object.values(giftVariantByLineKey));
      const paidVariantIds = new Set();
      lines.forEach((l) => {
        if (!l.isGiftLine && l.cartVariantId) paidVariantIds.add(l.cartVariantId);
      });

      // 2) 全グループの計算結果を集約し、バリアント ID ごとの「あるべき数量」を求める
      /** @type {Record<string, number>} */
      const desiredByVariant = {};
      results.forEach((g) => {
        Object.keys(g.byLine || {}).forEach((k) => {
          const vid = giftVariantByLineKey[k];
          if (!vid) return;
          const pick = g.byLine[k].selectionPick || 0;
          desiredByVariant[vid] = (desiredByVariant[vid] || 0) + pick;
        });
      });

      // 3) 必ず最新カートを取得（stale snapshot による二重 add を防ぐ）
      if (typeof window.pdsInvalidateCartJsonCache === 'function') {
        window.pdsInvalidateCartJsonCache();
      }
      const cart = await fetchCartJson(3, { maxAge: 0 });

      // 同一バリアントが複数行ある場合は数量を合算し、余剰行は後で 0 にする
      /** @type {Record<string, { keys: string[], quantity: number }>} */
      const currentGiftLines = {};
      cart.items.forEach((item) => {
        if (!isAttachedGiftCartItem(item, activeGiftVariantIds, paidVariantIds)) return;
        const vid = String(item.variant_id);
        if (!currentGiftLines[vid]) {
          currentGiftLines[vid] = { keys: [], quantity: 0 };
        }
        currentGiftLines[vid].keys.push(item.key);
        currentGiftLines[vid].quantity += Number(item.quantity) || 0;
      });

      // 4) 差分を update（数量変更/削除）と add（新規追加）に振り分け
      /** @type {Record<string, number>} */
      const updates = {};
      /** @type {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} */
      const adds = [];

      Object.keys(desiredByVariant).forEach((vid) => {
        const desired = desiredByVariant[vid];
        const existing = currentGiftLines[vid];
        if (existing && existing.keys.length) {
          // 先頭行を desired に合わせ、重複行があれば削除（数量 0）
          const needsQtyFix = existing.quantity !== desired;
          const hasDupLines = existing.keys.length > 1;
          if (needsQtyFix || hasDupLines) {
            updates[existing.keys[0]] = desired;
            for (let i = 1; i < existing.keys.length; i++) {
              updates[existing.keys[i]] = 0;
            }
          }
        } else if (desired > 0) {
          adds.push({
            id: Number(vid),
            quantity: desired,
            properties: { _pds_attached_gift: '1' },
          });
        }
      });

      // 不要になった添付品は全行を数量 0 で削除
      Object.keys(currentGiftLines).forEach((vid) => {
        if (!(vid in desiredByVariant) || desiredByVariant[vid] === 0) {
          currentGiftLines[vid].keys.forEach((key) => {
            updates[key] = 0;
          });
        }
      });

      // 5) 差分がなければ API コールをスキップ
      const hasUpdates = Object.keys(updates).length > 0;
      const hasAdds = adds.length > 0;
      if (!hasUpdates && !hasAdds) {
        // 既に一致している＝同期成功扱い（直前の失敗フラグをクリア）
        _attachmentCartSyncFailed = false;
        return;
      }

      // 6) Shopify Cart API で反映
      /** @type {{ item_count?: number } | null} */
      let cartAfterSync = null;

      if (hasUpdates) {
        const updateResp = await fetchCartMutation(window.routes.cart_update_url, { updates });
        if (!updateResp.ok) throw new Error('Cart update failed: ' + updateResp.status);
        cartAfterSync = await updateResp.json();
        if (typeof window.pdsInvalidateCartJsonCache === 'function') {
          window.pdsInvalidateCartJsonCache();
        }
      }
      if (hasAdds) {
        const addResp = await fetchCartMutation(window.routes.cart_add_url, { items: adds });
        if (!addResp.ok) throw new Error('Cart add failed: ' + addResp.status);
        if (typeof window.pdsInvalidateCartJsonCache === 'function') {
          window.pdsInvalidateCartJsonCache();
        }
        try {
          cartAfterSync = await fetchCartJson(3, { maxAge: 0 });
        } catch (_) {
          /* バッジ更新は update 応答または次回同期に任せる */
        }
      }

      // 変異後は保留中の再計算用スナップショットを破棄（二重 add 防止）
      _recomputeAfterCartUpdateCartSnapshot = null;

      // 7) UI 更新（バッジは JSON、有料表は行の増減時のみ Section 再取得）
      const needsCartTableRefresh = hasAdds || Object.values(updates).some((q) => q === 0);
      const bubblePromise = applyCartIconBubbleFromCart(cartAfterSync);
      const tablePromise = needsCartTableRefresh ? refreshCartItemsSectionContents() : Promise.resolve();
      await Promise.all([bubblePromise, tablePromise]);
      renderAttachmentSelectionPanel();

      // 8) cartUpdate イベントを発行し、他の UI コンポーネントに通知
      // source を指定して自分自身のイベントによる無限ループを防止
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pds-attached-gifts', cartData: cartAfterSync });
      }
      _attachmentCartSyncFailed = false;
    } catch (err) {
      console.error('[pds-attached-gifts] カート反映エラー:', err);
      _attachmentCartSyncFailed = true;
    } finally {
      _syncInFlight = false;
      if (_pendingSync) {
        const next = _pendingSync;
        _pendingSync = null;
        if (typeof window.pdsInvalidateCartJsonCache === 'function') {
          window.pdsInvalidateCartJsonCache();
        }
        syncAttachmentGiftsToCart(next.results, next.lines);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 添付品選択パネルの描画
  //
  // .pds-attached-gifts-root 要素の innerHTML を毎回丸ごと書き換える方式。
  // Dawn テーマの cart-items テーブルと同じ列構成にして見た目を揃えている。
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * HTML エスケープ（XSS 防止用）。
   * @param {string} s
   * @returns {string}
   */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * data-pds-attached-gift-variant-options（JSON 配列）をパースする。
   * @param {string|null} raw
   * @returns {{ name: string, value: string }[]}
   */
  function parseAttachedGiftVariantOptionsAttr(raw) {
    if (!raw || !String(raw).trim()) return [];
    try {
      const parsed = JSON.parse(String(raw));
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((o) => ({
          name: String(o && o.name != null ? o.name : '').trim(),
          value: String(o && o.value != null ? o.value : '').trim(),
        }))
        .filter((o) => o.name && o.value);
    } catch {
      return [];
    }
  }

  /**
   * 通常カート行と同様のバリエーション表示 HTML（dl > .product-option）。
   * @param {{ name: string, value: string }[]} options
   * @returns {string}
   */
  function renderAttachedGiftVariantOptionsHtml(options) {
    if (!options.length) return '';
    const parts = ['<dl>'];
    options.forEach((o) => {
      parts.push('<div class="product-option">');
      parts.push(`<dt>${escapeHtml(o.name)}:</dt>`);
      parts.push(`<dd>${escapeHtml(o.value)}</dd>`);
      parts.push('</div>');
    });
    parts.push('</dl>');
    return parts.join('');
  }

  /**
   * 行キーごとにパネル用の商品名・バリエーション・フル表示名を返す。
   * @param {PdsCartLineForAttachment[]} lines
   * @returns {{
   *   productTitleByKey: Record<string, string>,
   *   variantOptionsByKey: Record<string, { name: string, value: string }[]>,
   *   fullTitleByKey: Record<string, string>,
   * }}
   */
  function attachmentPanelDisplayByKey(lines) {
    /** @type {Record<string, string>} */
    const productTitleByKey = {};
    /** @type {Record<string, { name: string, value: string }[]>} */
    const variantOptionsByKey = {};
    /** @type {Record<string, string>} */
    const fullTitleByKey = {};

    lines.forEach((l) => {
      const paid = (l.lineTitle || '').trim();
      const giftProduct = (l.attachedGiftProductTitle || '').trim();
      const giftFull = (l.attachedGiftDisplayTitle || '').trim();
      const options = l.attachedGiftVariantOptions || [];

      productTitleByKey[l.lineKey] = giftProduct || paid || '';
      if (options.length) variantOptionsByKey[l.lineKey] = options;
      fullTitleByKey[l.lineKey] = giftFull || giftProduct || paid || '';
    });

    return { productTitleByKey, variantOptionsByKey, fullTitleByKey };
  }

  /**
   * .pds-attached-gifts-root の中身を計算結果に基づいて再描画する。
   * 添付品なし・ルール無効・候補行なしのときは非表示にする。
   */
  function renderAttachmentSelectionPanel() {
    const roots = document.querySelectorAll('.pds-attached-gifts-root');
    if (!roots.length) return;

    const r = getRules();
    if (!r || r.enabled === false) {
      roots.forEach((root) => {
        root.innerHTML = '';
        root.hidden = true;
      });
      clearAttachmentCheckoutError();
      return;
    }

    const lines = parseAttachmentLinesFromCartForms();
    const { productTitleByKey, variantOptionsByKey, fullTitleByKey } = attachmentPanelDisplayByKey(lines);

    /** @type {Record<string, string>} */
    const giftImageByKey = {};
    lines.forEach((l) => {
      if (l.attachedGiftImageUrl) giftImageByKey[l.lineKey] = l.attachedGiftImageUrl;
    });
    const results = window.pdsComputeAttachmentAllocations(lines, r, undefined);

    /** @type {string[]} */
    const parts = [];

    const visibleGroups = results.filter((g) => {
      const keys = g.candidateLineKeys || Object.keys(g.byLine || {});
      return g.wholeSlot > 0 && keys.length > 0;
    });

    const anyBlock = visibleGroups.length > 0;

    const svgMinus = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-minus" viewBox="0 0 10 2"><path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"/></svg>';
    const svgPlus = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10"><path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/></svg>';

    if (anyBlock) {
      parts.push('<div class="cart__items pds-attached-gifts__items">');
      parts.push(
        '<p class="pds-attached-gifts__heading">添付品（数量をお選びください）</p>'
      );
      parts.push('<table class="cart-items pds-attached-gifts__table">');
      parts.push('<caption class="visually-hidden">添付品</caption>');
      parts.push('<thead>');
      parts.push('<tr>');
      parts.push('<th class="cart-items__heading-cell caption-with-letter-spacing" colspan="2" scope="col">製品</th>');
      parts.push('<th class="cart-items__heading-cell cart-items__heading--total medium-hide large-up-hide caption-with-letter-spacing" colspan="1" scope="col"></th>');
      parts.push('<th class="cart-items__heading-cell cart-items__heading--wide cart-items__heading--quantity small-hide caption-with-letter-spacing" colspan="1" scope="col">数量</th>');
      parts.push('<th class="cart-items__heading-cell cart-items__heading--total small-hide caption-with-letter-spacing" colspan="1" scope="col"></th>');
      parts.push('</tr>');
      parts.push('</thead>');
    }

    visibleGroups.forEach((g) => {
      const keys = g.candidateLineKeys || Object.keys(g.byLine || {});
      const gidEsc = escapeHtml(g.groupId);
      const hasChoice = g.wholeSlot > 0;

      parts.push(`<tbody class="pds-attached-gifts__group" data-pds-attachment-group="${gidEsc}">`);

      keys.forEach((k) => {
        const row = g.byLine[k];
        if (!row) return;
        const productName = escapeHtml(productTitleByKey[k] || '商品');
        const ariaName = escapeHtml(fullTitleByKey[k] || productTitleByKey[k] || '商品');
        const variantOptionsHtml = renderAttachedGiftVariantOptionsHtml(variantOptionsByKey[k] || []);
        const picked = row.selectionPick || 0;
        const maxPick = row.pickMax || 0;
        const canPickMore = hasChoice && picked < maxPick && g.remainderLeft > 0;
        const canPickLess = picked > 0;
        const imgUrl = giftImageByKey[k] || '';

        parts.push(`<tr class="pds-attached-gifts__line" data-pds-attachment-gift-line="${escapeHtml(k)}">`);

        // 画像セル
        parts.push('<td class="cart-item__media">');
        if (imgUrl) {
          parts.push('<a class="cart-item__link" aria-hidden="true" tabindex="-1"> </a>');
          parts.push('<div class="cart-item__image-container gradient global-media-settings">');
          parts.push(`<img src="${escapeHtml(imgUrl)}" class="cart-item__image" alt="${ariaName}" loading="lazy" width="120" height="120">`);
          parts.push('</div>');
        }
        parts.push('</td>');

        // 詳細セル
        parts.push('<td class="cart-item__details">');
        parts.push(`<p class="cart-item__name h4 break">${productName}</p>`);
        if (variantOptionsHtml) parts.push(variantOptionsHtml);
        parts.push('</td>');

        // 合計セル（SP用、空。通常商品と同じ列数を維持するため）
        parts.push('<td class="cart-item__totals right medium-hide large-up-hide"></td>');

        // 数量セル
        parts.push('<td class="cart-item__quantity">');
        parts.push('<div class="cart-item__quantity-wrapper quantity-popover-wrapper">');
        // quantity-input は name=minus/plus 前提の validateQtyRules があり、添付品は data-pds-act 委譲のため div を使う
        parts.push('<div class="quantity cart-quantity">');

        if (hasChoice) {
          parts.push(
            `<button type="button" class="quantity__button no-js-hidden" name="pds-attached-minus" data-pds-act="dec" data-pds-gid="${gidEsc}" data-pds-lk="${escapeHtml(k)}" ${canPickLess ? '' : 'disabled'}>`
          );
          parts.push('<span class="visually-hidden">減らす</span>');
          parts.push(`<span class="svg-wrapper">${svgMinus}</span>`);
          parts.push('</button>');
          parts.push(
            `<input class="quantity__input" type="number" step="1" value="${picked}" min="0" max="${maxPick}" aria-label="${ariaName} の添付品数量" data-pds-gid="${gidEsc}" data-pds-lk="${escapeHtml(k)}">`
          );
          parts.push(
            `<button type="button" class="quantity__button no-js-hidden" name="pds-attached-plus" data-pds-act="inc" data-pds-gid="${gidEsc}" data-pds-lk="${escapeHtml(k)}" ${canPickMore ? '' : 'disabled'}>`
          );
          parts.push('<span class="visually-hidden">増やす</span>');
          parts.push(`<span class="svg-wrapper">${svgPlus}</span>`);
          parts.push('</button>');
        } else {
          parts.push(`<input class="quantity__input" type="number" value="${picked}" disabled readonly>`);
        }

        parts.push('</div>');
        if (hasChoice && maxPick > 0) {
          parts.push(`<p class="pds-attached-gifts__pick-max">添付数は最大${maxPick}個です</p>`);
        }
        parts.push('</div>');
        parts.push('</td>');

        // 合計セル（PC用、空。通常商品と同じ列数を維持するため）
        parts.push('<td class="cart-item__totals right small-hide"></td>');

        parts.push('</tr>');
      });

      parts.push('</tbody>');
    });

    if (anyBlock) {
      parts.push('</table>');
      parts.push('</div>');
    }

    const html = parts.join('');
    const show = anyBlock;
    roots.forEach((root) => {
      root.innerHTML = show ? html : '';
      root.hidden = !show;
    });
    syncAttachmentCheckoutErrorWithSelection();
  }

  /**
   * パネル内の ± ボタンのクリックを処理するイベント委譲ハンドラ。
   * パネルは毎回 innerHTML で再生成されるため、document にリスナーを 1 つだけ付ける。
   * @param {MouseEvent} ev
   */
  function onDocumentClickForAttachmentPanel(ev) {
    const el = ev.target;
    if (!(el instanceof Element)) return;
    const t = el.closest('[data-pds-act]');
    if (!t || !t.closest('.pds-attached-gifts-root')) return;
    const act = t.getAttribute('data-pds-act');
    const gid = t.getAttribute('data-pds-gid');
    const lk = t.getAttribute('data-pds-lk');
    if (!act || !gid) return;
    if (act === 'inc' && lk) window.pdsAttachmentAdjustPick(gid, lk, 1);
    else if (act === 'dec' && lk) window.pdsAttachmentAdjustPick(gid, lk, -1);
  }

  /**
   * 添付品数量 input の直接入力（スマホのキーボード等）。± と同様に再計算・カート同期する。
   * @param {Event} ev
   */
  function onDocumentChangeForAttachmentQuantity(ev) {
    const input = ev.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('quantity__input')) return;
    if (!input.closest('.pds-attached-gifts-root')) return;

    const gid = input.getAttribute('data-pds-gid');
    const lk = input.getAttribute('data-pds-lk');
    if (!gid || !lk) return;

    const val = Math.max(0, Math.floor(Number(input.value) || 0));
    window.pdsAttachmentUserPicks = window.pdsAttachmentUserPicks || {};
    window.pdsAttachmentUserPicks[gid] = {
      ...(window.pdsAttachmentUserPicks[gid] || {}),
      [lk]: val,
    };
    recomputeAndSyncAttachmentGifts();
  }

  /** パネルを手動で再描画する公開 API */
  window.pdsRefreshAttachmentSelectionPanel = renderAttachmentSelectionPanel;

  // ─────────────────────────────────────────────────────────────────────────
  // ページ読み込み時: カートに残っている添付品から選択状態を復元
  //
  // チェックアウトから戻ったとき等、JS の変数はリセットされるが
  // カートには前回の添付品が残っている。その数量を読んで選択状態を再構築する。
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * カート API またはフォーム上の添付品数量から window.pdsAttachmentUserPicks を復元する。
   * 通常商品の数量変化で上限が変わっている可能性があるため、pickMax で再チェックする。
   * カート API 取得に失敗した場合は、DOM 上の添付品行数量でフォールバック復元する
   *（空 picks のまま未選択扱いにして誤ブロック／フェイルオープンするのを避ける）。
   * @param {{ items?: object[] } | null} [cartSnapshot] - 既に取得済みのカート JSON（あれば再 GET しない）
   * @returns {Promise<object|null>} 取得したカート JSON（再利用用）。API 未取得時は null
   */
  async function restorePicksFromCart(cartSnapshot) {
    const r = getRules();
    if (!r || r.enabled === false || !r.groups || !r.groups.length) return null;

    const lines = parseAttachmentLinesFromCartForms();
    if (!lines.length) return null;

    // lineKey → 添付品バリアント ID（有料行のメタ）
    /** @type {Record<string, string>} */
    const giftVidByLineKey = {};
    lines.forEach((l) => {
      if (l.attachedGiftVariantId) giftVidByLineKey[l.lineKey] = l.attachedGiftVariantId;
    });
    const knownGiftVids = new Set(Object.values(giftVidByLineKey));
    if (!knownGiftVids.size) return null;

    // 呼び出し側のスナップショットがあれば再利用。無ければカート API で取得する。
    let cart =
      cartSnapshot && Array.isArray(cartSnapshot.items) ? cartSnapshot : null;
    if (!cart) {
      try {
        cart = await fetchCartJson();
      } catch (_) {
        // API 失敗時はフォーム上の添付品行から数量を拾って復元（未選択ガードを正しく効かせる）
        applyRestoredPicksFromGiftQuantities(
          lines,
          r,
          collectGiftQtyFromFormLines(lines, knownGiftVids),
          giftVidByLineKey
        );
        return null;
      }
    }

    // カート内の添付品バリアントの数量を集計
    /** @type {Record<string, number>} */
    const giftQtyInCart = {};
    const paidVariantIds = new Set();
    lines.forEach((l) => {
      if (!l.isGiftLine && l.cartVariantId) paidVariantIds.add(l.cartVariantId);
    });
    cart.items.forEach((item) => {
      const vid = String(item.variant_id);
      if (!isAttachedGiftCartItem(item, knownGiftVids, paidVariantIds)) return;
      giftQtyInCart[vid] = (giftQtyInCart[vid] || 0) + item.quantity;
    });
    applyRestoredPicksFromGiftQuantities(lines, r, giftQtyInCart, giftVidByLineKey);
    return cart;
  }

  /**
   * カートフォーム上の添付品行（isGiftLine）からバリアント別数量を集計する。
   * @param {PdsCartLineForAttachment[]} lines
   * @param {Set<string>} knownGiftVids
   * @returns {Record<string, number>}
   */
  function collectGiftQtyFromFormLines(lines, knownGiftVids) {
    /** @type {Record<string, number>} */
    const giftQty = {};
    lines.forEach((l) => {
      if (!l.isGiftLine || !l.cartVariantId) return;
      if (!knownGiftVids.has(l.cartVariantId)) return;
      giftQty[l.cartVariantId] = (giftQty[l.cartVariantId] || 0) + l.quantity;
    });
    return giftQty;
  }

  /**
   * 添付品バリアント数量を各グループ・各行の選択に割り当て、pdsAttachmentUserPicks へ書き込む。
   * @param {PdsCartLineForAttachment[]} lines
   * @param {PdsAttachmentRules} r
   * @param {Record<string, number>} giftQtyInCart
   * @param {Record<string, string>} giftVidByLineKey
   */
  function applyRestoredPicksFromGiftQuantities(lines, r, giftQtyInCart, giftVidByLineKey) {
    if (!Object.keys(giftQtyInCart).length) return;

    // 空の選択状態で計算し、各行の pickMax を得る
    const results = computeAllAttachmentAllocations(lines, r, {});

    // カート内の数量を各グループ・各行に割り当てて復元
    // pickMax と wholeSlot で再チェック（通常商品の数量変化に対応するため）
    /** @type {Record<string, Record<string, number>>} */
    const restored = {};
    results.forEach((g) => {
      const keys = g.candidateLineKeys || [];
      if (!keys.length || g.wholeSlot <= 0) return;

      // このグループの候補行が使うバリアントごとに、カートから配れる残り数量を用意
      /** @type {Record<string, number>} */
      const pool = {};
      keys.forEach((k) => {
        const vid = giftVidByLineKey[k];
        if (vid && giftQtyInCart[vid] && !(vid in pool)) {
          pool[vid] = giftQtyInCart[vid];
        }
      });

      // グループ全体の上限を予算として管理
      let groupBudget = g.wholeSlot;
      /** @type {Record<string, number>} */
      const groupPicks = {};

      keys.forEach((k) => {
        const vid = giftVidByLineKey[k];
        if (!vid || !pool[vid] || groupBudget <= 0) {
          groupPicks[k] = 0;
          return;
        }
        const row = g.byLine[k];
        const maxForLine = row ? row.pickMax : 0;
        const give = Math.min(pool[vid], maxForLine, groupBudget);
        groupPicks[k] = give;
        pool[vid] -= give;
        groupBudget -= give;
      });

      if (Object.values(groupPicks).some((v) => v > 0)) {
        restored[g.groupId] = groupPicks;
      }
    });

    if (Object.keys(restored).length) {
      window.pdsAttachmentUserPicks = restored;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // デバッグログ
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * debug フラグが有効なときだけ計算結果をコンソールに出力する。
   */
  function logDebugIfEnabled() {
    const r = getRules();
    if (!r?.debug) return;
    const result = window.pdsGetAttachmentAllocationsFromForms();
    console.log('[pds-attached-gifts]', result, 'userPicks', window.pdsAttachmentUserPicks);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 初期化: DOMContentLoaded + cartUpdate リスナー
  // ─────────────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('click', onDocumentClickForAttachmentPanel);
    document.addEventListener('change', onDocumentChangeForAttachmentQuantity);
    _picksRestored = false;
    _picksRestorePromise = restorePicksFromCart()
      .then(async (cartSnapshot) => {
        logDebugIfEnabled();
        // 復元直後の再計算・カート同期が終わるまで待つ（同期前チェックアウトのレース防止）
        await recomputeAndSyncAttachmentGifts(cartSnapshot ? { cartSnapshot } : {});
        // 並行 cartUpdate で pending になった再計算／同期も消化してから完了扱いにする
        await waitForAttachmentWorkToSettle();
      })
      .finally(() => {
        _picksRestored = true;
        // 復元完了後に未選択なら notice 表示＋チェックアウト非活性（最低数量制限と同様）
        syncAttachmentCheckoutErrorWithSelection();
      });

    // チェックアウト直前:
    // 1) 初回選択復元が終わるまで待つ（復元前の誤った未選択判定を防ぐ）
    // 2) 添付品の最大付与数まで選び切っていない場合は遷移を止め、エラーを表示する
    // 3) 遷移前に最新 cart.js で添付品数量を再同期する（パネル 1・実カート 2 のずれ防止）
    // 4) 添付品同期が未完了なら待ってから進む（再開時に未選択を再チェック）
    // 対象: #cart / #CartDrawer-Form の checkout 送信
    document.addEventListener(
      'submit',
      (ev) => {
        const form = ev.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;

        const submitter = ev.submitter;
        const isCheckout =
          submitter &&
          (submitter.id === 'checkout' ||
            submitter.id === 'CartDrawer-Checkout' ||
            submitter.name === 'checkout');
        if (!isCheckout) return;

        // checkout 以外の submit は止めない（数量更新など）
        if (_checkoutSubmitGuard) {
          ev.preventDefault();
          ev.stopPropagation();
          return;
        }

        // 選択復元前は判定できないため、完了後に requestSubmit でやり直す
        if (!_picksRestored) {
          ev.preventDefault();
          ev.stopPropagation();
          _checkoutSubmitGuard = true;
          _picksRestorePromise.finally(() => {
            _checkoutSubmitGuard = false;
            retryCheckoutSubmit(form, submitter);
          });
          return;
        }

        const incomplete = getIncompleteAttachmentSelection();
        if (incomplete) {
          ev.preventDefault();
          ev.stopPropagation();
          showAttachmentCheckoutError(form, buildIncompleteAttachmentMessage(incomplete.remaining));
          return;
        }
        clearAttachmentCheckoutError();

        // 最終同期済みの再 submit → 失敗なし＆通信中でなければ遷移を許可
        if (_checkoutAttachmentReconciled) {
          _checkoutAttachmentReconciled = false;
          if (_attachmentCartSyncFailed) {
            ev.preventDefault();
            ev.stopPropagation();
            showAttachmentCheckoutError(form, buildAttachmentSyncFailedMessage());
            return;
          }
          if (!isAttachmentWorkBusy()) return;
          ev.preventDefault();
          ev.stopPropagation();
          _checkoutSubmitGuard = true;
          waitForAttachmentWorkToSettle()
            .then(() => {
              _checkoutSubmitGuard = false;
              markAttachmentBusyTimeoutAsFailed();
              if (_attachmentCartSyncFailed) {
                showAttachmentCheckoutError(form, buildAttachmentSyncFailedMessage());
                return;
              }
              _checkoutAttachmentReconciled = true;
              retryCheckoutSubmit(form, submitter);
            })
            .catch((err) => {
              console.error('[pds-attached-gifts] チェックアウト再待機に失敗:', err);
              _checkoutSubmitGuard = false;
              _attachmentCartSyncFailed = true;
              showAttachmentCheckoutError(form, buildAttachmentSyncFailedMessage());
            });
          return;
        }

        // 遷移前に必ず最新カートへ添付品を再同期（stale による数量ずれを吸収）
        ev.preventDefault();
        ev.stopPropagation();
        _checkoutSubmitGuard = true;
        _attachmentCartSyncFailed = false;
        recomputeAndSyncAttachmentGifts({})
          .then(() => waitForAttachmentWorkToSettle())
          .then(() => {
            // requestSubmit 前にガード解除（submit リスナーが同期で再入するため）
            _checkoutSubmitGuard = false;
            markAttachmentBusyTimeoutAsFailed();
            if (_attachmentCartSyncFailed) {
              showAttachmentCheckoutError(form, buildAttachmentSyncFailedMessage());
              return;
            }
            const stillIncomplete = getIncompleteAttachmentSelection();
            if (stillIncomplete) {
              showAttachmentCheckoutError(
                form,
                buildIncompleteAttachmentMessage(stillIncomplete.remaining)
              );
              return;
            }
            _checkoutAttachmentReconciled = true;
            retryCheckoutSubmit(form, submitter);
          })
          .catch((err) => {
            console.error('[pds-attached-gifts] チェックアウト前同期に失敗:', err);
            _checkoutSubmitGuard = false;
            _attachmentCartSyncFailed = true;
            showAttachmentCheckoutError(form, buildAttachmentSyncFailedMessage());
          });
      },
      true
    );
  });

  /**
   * 有料行の cart/change 応答待ち（main-cart-items が loading）のあいだは
   * Section 差し替えを避け、誤った行番号・数量エラー表示を防ぐ。
   * 連続 cartUpdate はデバウンスし、cart-items の応答 JSON を再利用して GET を減らす。
   * @param {{ cartData?: { items?: object[] } } | undefined} event
   */
  function recomputeAfterPaidCartUpdateSettled(event) {
    if (event?.cartData?.items) {
      _recomputeAfterCartUpdateCartSnapshot = event.cartData;
    }

    if (_recomputeAfterCartUpdateTimer) {
      clearTimeout(_recomputeAfterCartUpdateTimer);
    }

    _recomputeAfterCartUpdateTimer = setTimeout(async () => {
      _recomputeAfterCartUpdateTimer = null;
      const cartSnapshot = _recomputeAfterCartUpdateCartSnapshot;
      _recomputeAfterCartUpdateCartSnapshot = null;

      if (typeof window.pdsWaitForCartAjaxIdle === 'function') {
        await window.pdsWaitForCartAjaxIdle();
      } else if (typeof window.pdsIsCartAjaxBusy === 'function') {
        let attempts = 0;
        while (window.pdsIsCartAjaxBusy() && attempts++ < 50) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }
      }

      logDebugIfEnabled();
      recomputeAndSyncAttachmentGifts(cartSnapshot ? { cartSnapshot } : {});
    }, 280);
  }

  // Dawn テーマの PubSub: カートが外部から更新されたらパネルを再描画
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // 自分自身の反映による cartUpdate では再同期しない（無限ループ防止）
      if (
        event &&
        (event.source === 'pds-attached-gifts' || event.source === 'pds-delivery-date')
      ) {
        return;
      }
      recomputeAfterPaidCartUpdateSettled(event);
    });
  }
})();
